import { motion } from 'framer-motion';
import { Plus, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { typography } from '../../styles/typography';
import { Card } from '../ui';
import { vendorApi } from './vendorApi';

// Generate dummy vendor data
const getDummyVendors = () => {
  return [
    {
      id: 1,
      vendorName: 'Apex Supplies Pvt Ltd',
      email: 'contact@apexsupplies.com',
      address: '123 Supply Street, Bangalore',
      city: 'Bangalore',
      state: 'Karnataka',
      country: 'India',
      pinCode: '560001',
      panNumber: 'ABCDE1234F',
      gstNumber: '29ABCDE1234F1Z5',
      bankName: 'State Bank of India',
      accountNo: '1234567890',
      ifscNo: 'SBIN0001234',
      sites: ['Bangalore Main Office', 'Mumbai Branch'],
      itemsProcure: ['Raw Materials', 'Packaging', 'Equipment'],
      createdAt: '2024-01-15',
    },
    {
      id: 2,
      vendorName: 'BluePeak Warehousing',
      email: 'info@bluepeak.com',
      address: '456 Warehouse Road, Hyderabad',
      city: 'Hyderabad',
      state: 'Telangana',
      country: 'India',
      pinCode: '500032',
      panNumber: 'FGHIJ5678K',
      gstNumber: '36FGHIJ5678K2Z6',
      bankName: 'HDFC Bank',
      accountNo: '9876543210',
      ifscNo: 'HDFC0009876',
      sites: ['Hyderabad Office'],
      itemsProcure: ['Storage Solutions', 'Logistics'],
      createdAt: '2024-02-20',
    },
    {
      id: 3,
      vendorName: 'Core Industrial Traders',
      email: 'support@coreindustrial.com',
      address: '789 Industrial Area, Mumbai',
      city: 'Mumbai',
      state: 'Maharashtra',
      country: 'India',
      pinCode: '400001',
      panNumber: 'KLMNO9012P',
      gstNumber: '27KLMNO9012P3Z7',
      bankName: 'ICICI Bank',
      accountNo: '5555666677',
      ifscNo: 'ICIC0005555',
      sites: ['Mumbai Branch', 'Pune Office'],
      itemsProcure: ['Industrial Equipment', 'Raw Materials', 'Tools'],
      createdAt: '2024-03-10',
    },
  ];
};

const VendorListView = () => {
  const navigate = useNavigate();
  const { openOverlay } = useOverlay();
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active'); // Filter by active vendors by default

  // Fetch vendors from API
  useEffect(() => {
    const fetchVendors = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const result = await vendorApi.listVendors({
          limit: 100, // Max limit as per API spec
          status: statusFilter,
          name_contains: searchTerm.trim() || undefined,
        });

        if (result.success) {
          // Map API response to component format
          const mappedVendors = result.vendors.map((vendor) => ({
            id: vendor.vendor_id,
            vendor_id: vendor.vendor_id,
            vendorName: vendor.vendor_name || '',
            email: vendor.contact_email || vendor.billing_address?.email || '',
            address: vendor.billing_address?.street || '',
            city: vendor.billing_address?.city || '',
            state: vendor.billing_address?.state || '',
            country: vendor.billing_address?.country || '',
            pinCode: vendor.billing_address?.pincode || '',
            panNumber: vendor.pan_number || vendor.billing_address?.pan_number || '',
            gstNumber: vendor.gst_number || '',
            bankName: vendor.bank_details?.bank_name || '',
            accountNo: vendor.bank_details?.account_number || '',
            ifscNo: vendor.bank_details?.ifsc_code || '',
            sites: vendor.site_ids?.map((siteId, idx) => 
              vendor.site_name || `Site ${idx + 1}`
            ) || [],
            site_ids: vendor.site_ids || [], // Preserve site_ids for overlay to resolve site names
            itemsProcure: vendor.items_supplied || [],
            createdAt: vendor.created_at || new Date().toISOString(),
            // Additional fields from API
            contactPerson: vendor.contact_person || '',
            contactPhone: vendor.contact_phone || '',
            secondaryContact: vendor.secondary_contact || null,
            status: vendor.status || 'active',
            leadTimeDays: vendor.lead_time_days,
            paymentTerms: vendor.payment_terms,
          }));

          setVendors(mappedVendors);
        } else {
          setError(result.message || 'Failed to load vendors');
          setVendors([]);
        }
      } catch (err) {
        console.error('Error fetching vendors:', err);
        setError(err.message || 'Failed to load vendors');
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };

    // Debounce search to avoid too many API calls
    const timeoutId = setTimeout(() => {
      fetchVendors();
    }, searchTerm.trim() ? 500 : 0); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter]);

  // Client-side filtering for additional fields (email, city, PAN, GST) if needed
  const filteredVendors = useMemo(() => {
    if (!searchTerm.trim()) {
      return vendors;
    }
    
    // If API already filtered by name_contains, do additional client-side filtering
    const term = searchTerm.toLowerCase();
    return vendors.filter(
      (vendor) =>
        vendor.vendorName.toLowerCase().includes(term) ||
        vendor.email.toLowerCase().includes(term) ||
        vendor.city.toLowerCase().includes(term) ||
        vendor.panNumber.toLowerCase().includes(term) ||
        vendor.gstNumber.toLowerCase().includes(term)
    );
  }, [vendors, searchTerm]);

  const handleDownload = (vendor) => {
    const csvData = [
      ['Vendor Name', 'Email', 'Contact Person', 'Contact Phone', 'Address', 'City', 'State', 'Country', 'Pin Code', 'PAN', 'GST', 'Bank', 'Account No', 'IFSC', 'Sites', 'Items', 'Status', 'Lead Time (Days)', 'Payment Terms', 'Created At'],
      [
        vendor.vendorName || '',
        vendor.email || '',
        vendor.contactPerson || '',
        vendor.contactPhone || '',
        vendor.address || '',
        vendor.city || '',
        vendor.state || '',
        vendor.country || '',
        vendor.pinCode || '',
        vendor.panNumber || '',
        vendor.gstNumber || '',
        vendor.bankName || '',
        vendor.accountNo || '',
        vendor.ifscNo || '',
        Array.isArray(vendor.sites) ? vendor.sites.join('; ') : '',
        Array.isArray(vendor.itemsProcure) ? vendor.itemsProcure.join('; ') : '',
        vendor.status || 'active',
        vendor.leadTimeDays || '',
        vendor.paymentTerms || '',
        vendor.createdAt ? new Date(vendor.createdAt).toLocaleDateString() : '',
      ],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateStr = vendor.createdAt ? new Date(vendor.createdAt).toISOString().split('T')[0] : 'unknown';
    a.download = `vendor-${vendor.vendorName?.replace(/\s+/g, '-') || 'vendor'}-${dateStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className={typography.h2} style={{ color: '#000000' }}>
            Vendors
          </h1>
          <p className="mt-2 text-gray-600">
            Manage and view all onboarded vendors
          </p>
        </div>
        <button
          onClick={() => navigate('/onboarding-vender-onboarding/create')}
          className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
        >
          <Plus className="w-4 h-4" />
          Onboard Vendor
        </button>
      </div>

      {/* Search Bar and Status Filter */}
      <div className="mb-6 flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search vendors by name, email, city, PAN, or GST..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20"
          />
        </div>
        <div className="min-w-[150px]">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3 px-4 text-sm text-gray-900 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/20"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="">All</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <Card.Light className="p-8 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <p className="text-gray-500">Loading vendors...</p>
          </div>
        </Card.Light>
      )}

      {/* Error State */}
      {error && !loading && (
        <Card.Light className="p-8 text-center">
          <p className="text-red-600 text-lg">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 text-sm text-blue-600 hover:text-blue-800 underline"
          >
            Retry
          </button>
        </Card.Light>
      )}

      {/* Vendors List */}
      {!loading && !error && (
        <div className="space-y-4">
          {filteredVendors.length > 0 ? (
          filteredVendors.map((vendor, index) => (
            <motion.div
              key={vendor.id}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
            >
              <Card.Light
                className="p-6 cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => openOverlay('vendorDetails', {
                  vendor: vendor,
                  onDownload: handleDownload
                })}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold">
                        {index + 1}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800">
                        {vendor.vendorName}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      {vendor.email}
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Location: </span>
                        <span className="font-medium">
                          {vendor.city}, {vendor.state}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">PAN: </span>
                        <span className="font-medium">{vendor.panNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">GST: </span>
                        <span className="font-medium">{vendor.gstNumber}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Sites: </span>
                        <span className="font-medium">
                          {vendor.sites.length} site{vendor.sites.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600">Bank: </span>
                        <span className="font-medium">{vendor.bankName}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Account: </span>
                        <span className="font-medium">{vendor.accountNo}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">IFSC: </span>
                        <span className="font-medium">{vendor.ifscNo}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Created: </span>
                        <span className="font-medium">
                          {new Date(vendor.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload(vendor);
                      }}
                      className="w-10 h-10 p-2 bg-blue-700 rounded flex justify-center items-center gap-2.5 cursor-pointer hover:bg-blue-800 transition-colors"
                      title="Download"
                    >
                      <Download className="w-6 h-6 text-white stroke-white" strokeWidth={2} />
                    </button>
                  </div> */}
                </div>
              </Card.Light>
            </motion.div>
          ))
        ) : (
          <Card.Light className="p-8 text-center">
            <p className="text-gray-500 text-lg">
              No vendors found matching your search criteria.
            </p>
          </Card.Light>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorListView;
